import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useCreateBillingDocument } from '../../hooks/queries/useBillingDocuments';
import {
  formatEuros,
  replaceBillingLines,
  type BillingDocumentType,
  type BillingLineDraft,
  type BillingLineUnit,
} from '../../services/billing/billingService';
import { useDossier, useDossiers } from '../../hooks/queries/useDossiers';
import { useDossierContacts } from '../../hooks/queries/useContacts';
import { getContactDisplayName } from '../../services/contacts/contactService';
import { LINE_NATURE_LABELS, type LineNature } from '../../lib/billing/lineTotals';

const UNITS: { value: BillingLineUnit; label: string }[] = [
  { value: 'hour', label: 'h' },
  { value: 'item', label: 'unité' },
  { value: 'forfait', label: 'forfait' },
];

type DraftLine = BillingLineDraft & { key: string };

function emptyLine(sort: number): DraftLine {
  return {
    key: crypto.randomUUID(),
    description: '',
    quantity: 1,
    unit: 'item',
    unit_price: 0,
    sort_order: sort,
    nature: 'honoraires',
  };
}

interface Props {
  dossierId?: string;
  docType?: Extract<BillingDocumentType, 'invoice' | 'estimate'>;
  onCancel: () => void;
}

export function NewPolarQuotePanel({ dossierId, docType = 'estimate', onCancel }: Props) {
  const navigate = useNavigate();
  const [pickedDossier, setPickedDossier] = useState(dossierId ?? '');
  const effectiveDossierId = dossierId ?? (pickedDossier || undefined);
  const { data: dossier } = useDossier(effectiveDossierId);
  const { data: dossiers } = useDossiers();
  const { data: parties } = useDossierContacts(effectiveDossierId);
  const createMut = useCreateBillingDocument();
  const clients = useMemo(
    () => (parties ?? []).filter((p) => p.role === 'client' && p.contact),
    [parties],
  );

  const [clientId, setClientId] = useState('');
  const isInvoice = docType === 'invoice';
  const [vatRate, setVatRate] = useState('20');
  const [title, setTitle] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(1)]);

  const defaultTitle = dossier ? `Honoraires — ${dossier.number} ${dossier.title}` : '';
  const titleValue = title || defaultTitle;
  const selectedClient = clientId || clients[0]?.contact_id || '';

  const ht = lines.reduce((s, l) => s + Number(l.quantity) * Number(l.unit_price), 0);
  const vat = ht * (Number(vatRate) / 100);
  const ttc = ht + vat;

  function patchLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  async function submit() {
    if (!effectiveDossierId) {
      toast.error('Choisissez un dossier.');
      return;
    }
    if (!selectedClient) {
      toast.error('Choisissez un client du dossier.');
      return;
    }
    const cleaned = lines
      .filter((l) => l.description.trim())
      .map((l, i) => ({
        description: l.description.trim(),
        quantity: Number(l.quantity) || 0,
        unit: l.unit,
        unit_price: Number(l.unit_price) || 0,
        sort_order: i + 1,
        nature: l.nature ?? 'honoraires',
      }));
    if (cleaned.length === 0) {
      toast.error('Ajoutez au moins une ligne.');
      return;
    }
    try {
      const doc = await createMut.mutateAsync({
        dossier_id: effectiveDossierId,
        doc_type: docType,
        client_contact_id: selectedClient,
        vat_rate: Number(vatRate) || 20,
        notes: titleValue,
      });
      await replaceBillingLines(doc.id, cleaned);
      toast.success(isInvoice ? 'Facture brouillon créée' : 'Devis brouillon créé');
      navigate(`/facturation/${doc.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2
          className="text-2xl font-bold text-cime-primary"
          style={{ fontFamily: 'var(--cime-font-heading)' }}
        >
          {isInvoice ? 'Nouvelle facture' : 'Nouveau devis'}
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Lignes libres, sans feuille de temps. Polar numérote au figer.
        </p>
      </div>

      <div className="rounded-cime border border-neutral-200 bg-white p-4 grid grid-cols-1 md:grid-cols-5 gap-4">
        {!dossierId && (
          <div className="md:col-span-5">
            <label className="text-sm font-medium text-neutral-700" htmlFor="polar-dossier">
              Dossier
            </label>
            <select
              id="polar-dossier"
              className="mt-1 w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent"
              value={pickedDossier}
              onChange={(e) => {
                setPickedDossier(e.target.value);
                setClientId('');
              }}
            >
              <option value="">Choisir un dossier</option>
              {(dossiers ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.number} — {d.title}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="md:col-span-3">
          <label className="text-sm font-medium text-neutral-700" htmlFor="polar-client">
            Client
          </label>
          <select
            id="polar-client"
            className="mt-1 w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent"
            value={selectedClient}
            onChange={(e) => setClientId(e.target.value)}
          >
            {clients.length === 0 && <option value="">Aucun client sur le dossier</option>}
            {clients.map((p) => (
              <option key={p.contact_id} value={p.contact_id}>
                {getContactDisplayName(p.contact)}
              </option>
            ))}
          </select>
          <p className="text-xs text-neutral-500 mt-1">Partie du dossier, créée dans Polar au premier figer.</p>
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-neutral-700" htmlFor="polar-vat">
            Taux de TVA
          </label>
          <select
            id="polar-vat"
            className="mt-1 w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent"
            value={vatRate}
            onChange={(e) => setVatRate(e.target.value)}
          >
            <option value="20">20 %</option>
            <option value="10">10 %</option>
            <option value="5.5">5,5 %</option>
            <option value="0">0 %</option>
          </select>
        </div>
        <div className="md:col-span-5">
          <Input
            id="polar-title"
            label="Intitulé"
            value={titleValue}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-cime border border-neutral-200 bg-white p-4">
        <p className="text-sm font-semibold text-neutral-700 mb-3">Lignes</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-neutral-500">
              <th className="text-left font-medium pb-2 pr-2">Désignation</th>
              <th className="text-left font-medium pb-2 px-2 w-32">Nature</th>
              <th className="text-right font-medium pb-2 px-2 w-24">Qté</th>
              <th className="text-left font-medium pb-2 px-2 w-28">Unité</th>
              <th className="text-right font-medium pb-2 px-2 w-28">PU HT</th>
              <th className="text-right font-medium pb-2 pl-2 w-28">Total HT</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key} className="border-t border-neutral-100">
                <td className="py-2 pr-2">
                  <textarea
                    rows={1}
                    className="w-full min-h-9 px-3 py-2 text-sm rounded-cime border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-cime-primary"
                    placeholder="Prestation, honoraires, frais…"
                    value={l.description}
                    onChange={(e) => patchLine(l.key, { description: e.target.value })}
                  />
                </td>
                <td className="py-2 px-2">
                  <select
                    className="w-full h-9 px-2 text-sm rounded-cime border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-cime-primary"
                    aria-label="Nature de la ligne"
                    value={l.nature ?? 'honoraires'}
                    onChange={(e) => patchLine(l.key, { nature: e.target.value as LineNature })}
                  >
                    {(Object.keys(LINE_NATURE_LABELS) as LineNature[]).map((n) => (
                      <option key={n} value={n}>{LINE_NATURE_LABELS[n]}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-2">
                  <input
                    className="w-full h-9 px-2 text-sm text-right rounded-cime border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-cime-primary"
                    inputMode="decimal"
                    value={l.quantity}
                    onChange={(e) => patchLine(l.key, { quantity: Number(e.target.value) })}
                  />
                </td>
                <td className="py-2 px-2">
                  <select
                    className="w-full h-9 px-2 text-sm rounded-cime border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-cime-primary"
                    value={l.unit}
                    onChange={(e) => patchLine(l.key, { unit: e.target.value as BillingLineUnit })}
                  >
                    {UNITS.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-2">
                  <input
                    className="w-full h-9 px-2 text-sm text-right rounded-cime border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-cime-primary"
                    inputMode="decimal"
                    value={l.unit_price}
                    onChange={(e) => patchLine(l.key, { unit_price: Number(e.target.value) })}
                  />
                </td>
                <td className="py-2 pl-2 text-right tabular-nums text-neutral-600">
                  {formatEuros(Number(l.quantity) * Number(l.unit_price))}
                </td>
                <td className="py-2">
                  <button
                    type="button"
                    className="w-8 h-8 rounded-cime text-neutral-400 hover:bg-red-50 hover:text-red-700"
                    aria-label="Supprimer la ligne"
                    onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-start justify-between gap-4 mt-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setLines((prev) => [...prev, emptyLine(prev.length + 1)])}
          >
            Ajouter une ligne
          </Button>
          <div className="w-64 text-sm text-neutral-500 space-y-1">
            <div className="flex justify-between"><span>HT</span><span>{formatEuros(ht)}</span></div>
            <div className="flex justify-between"><span>TVA {vatRate} %</span><span>{formatEuros(vat)}</span></div>
            <div className="flex justify-between font-semibold text-cime-primary border-t border-neutral-200 pt-2">
              <span>TTC</span><span>{formatEuros(ttc)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="primary" onClick={submit} isLoading={createMut.isPending}>
          {isInvoice ? 'Créer la facture' : 'Créer le devis'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
}
